/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { WireCountry, WiresService } from '@treasury/domain/wires';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import type { DateRange } from '@treasury/presentation/components/tm-date-range';
import { DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-radio-group';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

const defaultCreatedDateRange: DateRange = {
    rangeType: DateRangeType.None,
};

export const initialCreditorFilters = {
    requireEntitlement: true,
    beneficiaryType: 'Both',
    status: 'All',
    beneficiaryName: '',
    accountNumber: '',
    bankId: '',
    bankName: '',
    country: 'All',
    createdDateType: 'Specific Date',
    createdDateStart: '',
    createdDateEnd: '',
    createdDate: '',
};

export const tagName = 'creditors-filter-sheet';
@customElement(tagName)
export class CreditorsFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property({ type: Boolean })
    public isCreditorList = true;

    @InjectProperty()
    declare private wiresService: WiresService;

    @state()
    private loading = false;

    @state()
    private wireCountries: WireCountry[] = [];

    @state()
    private wireCountryOptions: Array<{ label: string; value: string }> = [];

    @state()
    private initialFilters = { ...initialCreditorFilters };

    @DeepReactive()
    private filters = { ...this.initialFilters };

    @state()
    private createdDateRange: DateRange = defaultCreatedDateRange;

    @state()
    private buttonConfig: ButtonConfig[] = [
        {
            text: 'Reset',
            importance: 'secondary',
            onClick: () => {
                this.resetFilters();
            },
        },
        {
            text: 'Apply Filters',
            onClick: () => {
                this.applyFilters();
                this.open = false;
            },
        },
    ];

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new Event('close'));
            }}
            ><div slot="header-center">Filter Creditors</div>
            <div slot="header-right"></div>
            <div>
                ${this.renderFilters()}
                <tm-footer .buttonConfig=${this.buttonConfig}></tm-footer></div
        ></tm-bottom-sheet>`;
    }

    protected renderFilters() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<tm-section class="p-3 mb-10">
            <tm-radio-group
                .initial=${this.filters.beneficiaryType}
                label="Creditor type"
                .options=${[
                    { label: 'Both', value: 'Both' },
                    { label: 'Domestic', value: 'Domestic' },
                    { label: 'International', value: 'International' },
                ]}
                @value-changed=${(e: CustomEvent) => {
                    this.filters.beneficiaryType = e.detail.value;
                }}
            ></tm-radio-group>
            <tm-select
                label="Status"
                .initial=${this.filters.status}
                .items=${[
                    {
                        label: 'All',
                        value: 'All',
                    },
                    { label: 'Pending Approval', value: 'Pending Approval' },
                    { label: 'Rejected', value: 'Rejected' },
                    { label: 'Ready', value: 'Ready' },
                ]}
                @value-changed=${(e: CustomEvent) => {
                    if (!e.detail.value) {
                        return;
                    }
                    this.filters.status = e.detail.value;
                }}
            ></tm-select>
            <tm-text-field
                label="Creditor name"
                .initial=${this.filters.beneficiaryName}
                @value-changed=${(e: CustomEvent) => {
                    this.filters.beneficiaryName = e.detail.value;
                }}
            ></tm-text-field>
            <tm-text-field
                label="Account number"
                .initial=${this.filters.accountNumber}
                @value-changed=${(e: CustomEvent) => {
                    this.filters.accountNumber = e.detail.value;
                }}
            ></tm-text-field>
            <tm-text-field
                label="Agent ID"
                .initial=${this.filters.bankId}
                @value-changed=${(e: CustomEvent) => {
                    this.filters.bankId = e.detail.value;
                }}
            ></tm-text-field>
            <tm-text-field
                label="Agent name"
                .initial=${this.filters.bankName}
                @value-changed=${(e: CustomEvent) => {
                    this.filters.bankName = e.detail.value;
                }}
            ></tm-text-field>
            <tm-select
                label="Country"
                .items=${this.wireCountryOptions}
                .initial=${this.filters.country}
                @value-changed=${(e: CustomEvent) => {
                    this.filters.country = e.detail.value;
                }}
            ></tm-select>
            <tm-date-range
                label="Created date"
                .initial=${this.createdDateRange}
                .maxDate=${new Date()}
                .dateRangeOptions=${[DateRangeType.SpecificDate, DateRangeType.DateRange]}
                @value-changed=${(e: CustomEvent) => {
                    const { value, isValid } = e.detail;
                    if (!isValid) {
                        return;
                    }
                    if (value) {
                        this.createdDateRange = value;
                        this.filters.createdDateType = value.rangeType;
                        this.filters.createdDateStart =
                            value.startDate && value.endDate ? value.startDate.toISOString() : '';
                        this.filters.createdDate =
                            value.rangeType === DateRangeType.SpecificDate && value.startDate
                                ? value.startDate.toISOString()
                                : '';
                        this.filters.createdDateEnd = value.endDate
                            ? value.endDate.toISOString()
                            : '';
                    }
                }}
            ></tm-date-range>
        </tm-section>`;
    }

    protected async firstUpdated() {
        this.loading = true;
        this.wireCountries = await this.wiresService.getWireCountries();
        this.wireCountryOptions = this.wireCountries.map(country => ({
            label: country.countryName,
            value: country.countryCode,
        }));
        this.wireCountryOptions.unshift({ label: 'All', value: 'All' });
        this.loading = false;
    }

    private applyFilters() {
        const filters = {
            ...this.filters,
            country: this.filters.country === 'All' ? '' : this.filters.country,
        };
        this.dispatchEvent(new CustomEvent('apply-filters', { detail: filters }));
    }

    private resetFilters() {
        this.filters = { ...this.initialFilters };
        this.createdDateRange = { ...defaultCreatedDateRange };
        this.requestUpdate();
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'creditors-filter-sheet': CreditorsFilterSheet;
    }
}
